/**
 * FocusPreviewHeader
 * Simplified header for tabbed Focus Preview pane
 * Shows: ID [severity] Title, then Age/Category/Tags (alpha sorted) metadata
 */

import { makeStyles, tokens, Text, Badge, Caption1 } from '@fluentui/react-components'
import type { IssueDetails } from '../../../domain'
import { formatCategory } from '../../../utils/formatters'
import { getSeverityBadgeColor } from '../../../utils/colorUtils'
import { TagBadges } from '../../../components'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    // Aligned padding with tabs for unified surface
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    // Flat background for unified surface with tabs
    backgroundColor: tokens.colorBrandBackground2,
    // No shadow - unified surface
    boxShadow: 'none',
    flexShrink: 0,
    position: 'relative',
    zIndex: 1,
  },
  topRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
    // Transparent - inherits parent background
    backgroundColor: 'transparent',
    // Brand accent left border (kept for visual anchor)
    borderLeft: `3px solid ${tokens.colorBrandBackground}`,
    // No border radius - removes "pill" effect
    borderRadius: 0,
    // Cleaner padding alignment
    paddingLeft: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
    // No shadow - unified surface
    boxShadow: 'none',
  },
  issueId: {
    fontFamily: 'monospace',
    // Slightly larger issue ID
    fontSize: tokens.fontSizeBase300,
    // Subtle brand tint for emphasis
    color: tokens.colorBrandForeground2,
    fontWeight: tokens.fontWeightMedium,
  },
  title: {
    lineHeight: tokens.lineHeightBase400,
    // Increased contrast for title
    color: tokens.colorNeutralForeground1,
  },
  metadataRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
    color: tokens.colorNeutralForeground3,
    // Improved visual grouping with top margin
    marginTop: tokens.spacingVerticalXXS,
  },
  metadataItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
  },
  tagsInline: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
})

export interface FocusPreviewHeaderProps {
  issue: IssueDetails
}

export function FocusPreviewHeader({ issue }: FocusPreviewHeaderProps) {
  const styles = useStyles()

  return (
    <div className={styles.root}>
      <div className={styles.topRow}>
        <span className={styles.issueId}>{issue.id}</span>
        <Badge color={getSeverityBadgeColor(issue.severity)} appearance="filled" size="small">
          {issue.severity}
        </Badge>
        <Text weight="semibold" size={400} className={styles.title}>
          {issue.title}
        </Text>
      </div>
      <div className={styles.metadataRow}>
        <Caption1 className={styles.metadataItem}>
          <span>Age:</span>
          <span>{issue.ageDisplay}</span>
        </Caption1>
        <Caption1 className={styles.metadataItem}>
          <span>Category:</span>
          <span>{formatCategory(issue.category)}</span>
        </Caption1>
        {issue.tags.length > 0 && (
          <TagBadges
            tags={[...issue.tags].sort((a, b) => a.label.localeCompare(b.label))}
            className={styles.tagsInline}
          />
        )}
      </div>
    </div>
  )
}
